/**
 * English (en) translations for the END-USER app only.
 *
 * The admin app keeps its own, completely separate translations at
 * `apps/admin/src/i18n/locales/en.json` (different i18n instance, JSON format).
 * Do NOT add admin-side keys here, and vice versa — the two never overlap.
 */
export default {
  common: {
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    retry: 'Retry',
    loading: 'Loading...',
    confirm: 'Confirm',
    continue: 'Continue',
    notAvailable: 'Not available',
    skipToContent: 'Skip to main content',
    all: 'All',
    other: 'Other',
    processing: 'Processing...',
    viewAll: 'View all',
  },

  dates: {
    relative: {
      now: 'just now',
      yesterday: 'yesterday',
      today: 'today',
      tomorrow: 'tomorrow',
      minuteAgo: '1 minute ago',
      minutesAgo: '{n} minutes ago',
      hourAgo: '1 hour ago',
      hoursAgo: '{n} hours ago',
      daysAgo: '{n} days ago',
      weekAgo: '1 week ago',
      weeksAgo: '{n} weeks ago',
      monthAgo: '1 month ago',
      monthsAgo: '{n} months ago',
      inDays: 'in {n} days',
      inWeeks: 'in {n} weeks',
    },
    remaining: {
      finished: 'Finished',
      endsToday: 'Ends today',
      endsTomorrow: 'Ends tomorrow',
      daysLeft: '{n} days left',
      weeksLeft: '{n} weeks left',
      monthsLeft: '{n} months left',
    },
  },

  errors: {
    login: 'Failed to sign in',
    register: 'Registration failed',
    registerOwner: 'Failed to register the owner',
    registerEmployee: 'Failed to register the employee',
    requestPasswordReset: 'Failed to request password reset',
    resetPassword: 'Failed to reset password',
    updateProfile: 'Failed to update profile',
    loadPharmacy: 'Failed to load pharmacy',
    nifRequiredOwner: 'The CIF/NIF is required to register the owner',
    activationCodeRequired: 'The activation code is required to register the owner',
    nifRequiredEmployee: 'The CIF/NIF is required to register the employee',
    noActiveSession: 'No active session',
    loadTickets: 'Failed to load tickets',
    loadTicket: 'Failed to load ticket',
    loadTicketSummary: 'Failed to load ticket summary',
    loadRecentTickets: 'Failed to load recent tickets',
    loadTicketBatch: 'Failed to load ticket batch',
    uploadTicket: 'Failed to upload ticket',
    noTicketCreated: 'No ticket was created',
    selectChallenge: 'Select at least one challenge',
    ticketBatchFailed: 'Could not create the ticket batch. Please try again.',
    loadChallenges: 'Failed to load challenges',
    loadChallenge: 'Failed to load challenge',
    loadBrands: 'Failed to load brands',
    loadNotifications: 'Failed to load notifications',
    updateNotification: 'Failed to update notification',
    markNotificationRead: 'Failed to mark notification as read',
    loadBalance: 'Failed to load balance',
    loadSummary: 'Failed to load summary',
    loadBreakdown: 'Failed to load breakdown',
    loadContributions: 'Failed to load contributions',
    loadContributionsSummary: 'Failed to load contributions summary',
    loadOwnerSummary: 'Failed to load owner summary',
    loadContributorBreakdown: 'Failed to load contributor breakdown',
    loadOwnerMovements: 'Failed to load owner movements',
    invalidBalanceResponse: 'Invalid balance response',
    invalidOwnerSummaryResponse: 'Invalid owner summary response',
    invalidOwnerMovementsResponse: 'Invalid owner movements response',
    loadApprovals: 'Failed to load approval requests',
    approveRequest: 'Failed to approve the request',
    rejectRequest: 'Failed to reject the request',
    gdprExport: 'Failed to export personal data',
    gdprDelete: 'Failed to run data deletion',
    withdrawalRemoved: 'Payouts are no longer made within the app.',
    payoutExternal: 'Payouts are processed externally',
    bankAccountRemoved: 'Bank account management is no longer available in this app.',
  },

  routes: {
    login: 'Sign In',
    register: 'Register',
    registerPending: 'Registration pending',
    forgotPassword: 'Recover Password',
    terms: 'Terms and Conditions',
    legalBases: 'Legal Bases',
    dashboard: 'Dashboard',
    challenges: 'Challenges',
    ticketStatus: 'Ticket Status',
    cashBalance: 'Balance',
    profile: 'Profile',
    notifications: 'Notifications',
    privacy: 'Privacy',
    ownerDashboard: 'Owner Dashboard',
    employeeApprovals: 'Employee Approvals',
    notFound: 'Page not found',
  },

  dashboard: {
    greeting: 'Hello, {name}!',
    defaultUserName: 'User',
    loading: 'Loading dashboard...',
    pharmacyTotalEarnings: 'Your pharmacy total earnings',
    yourContribution: 'Your contribution',
    yourBalance: 'Your Available Balance',
    viewDetails: 'View details',
    pending: 'Pending',
    tickets: 'Receipts',
    activeChallenges: 'Active challenges',
    featuredChallenges: 'Featured challenges',
    noActiveChallenges: 'No active challenges',
    noActiveChallengesDesc: 'New challenges will appear here when available',
    recentActivity: 'Recent activity',
    ticketFallback: 'Receipt',
    noRecentActivity: 'No recent activity',
    noRecentActivityDesc: 'Upload your first receipt to start earning rewards',
    exploreChallenges: 'Explore challenges',
  },

  language: {
    title: 'Language',
    spanish: 'Spanish',
    english: 'English',
    updated: 'Language updated',
    updateError: "Couldn't update language. Please try again.",
  },

  nav: {
    account: 'My account',
    home: 'Home',
    dashboard: 'Dashboard',
    challenges: 'Challenges',
    tickets: 'Receipts',
    balance: 'Balance',
    profile: 'Profile',
    notifications: 'Alerts',
    privacy: 'Privacy',
    approvals: 'Approvals',
    mainNavigation: 'Main navigation',
    navigation: 'Navigation',
    menu: 'Menu',
  },

  header: {
    rolePharmacist: 'Pharmacist',
    roleOwner: 'Owner',
    contact: 'Contact us',
    openContact: 'Open contact section',
    userMenu: 'User menu',
    myProfile: 'My Profile',
    logout: 'Log Out',
    login: 'Log In',
    logoAlt: 'P&G Health Contigo Incentives',
  },

  layout: {
    pageTitle: {
      Dashboard: 'Home',
      OwnerDashboard: 'Dashboard',
      Challenges: 'Challenges',
      TicketStatus: 'Receipts',
      CashBalance: 'Balance',
      Profile: 'My Profile',
      Notifications: 'Notifications',
      EmployeeApprovals: 'Approvals',
      Privacy: 'Privacy',
    },
    logout: 'Log out',
    mainAction: 'Main action',
    ticketsUploaded: 'Receipts uploaded',
    ticketsUploadedDetail: '{count} receipt uploaded successfully | {count} receipts uploaded successfully',
  },

  mobile: {
    upload: 'Upload',
    uploadTicketAria: 'Upload purchase receipt',
  },

  contact: {
    kicker: 'Contact us',
    heading: 'Have a question or need assistance?',
    text: 'We are here to help you.',
    sendEmailAria: 'Send an email to {email}',
    copyEmail: 'Copy email',
    emailCopied: 'Email copied',
    responseTime: 'Our team will reply to your message within 24–48 business hours.',
  },

  footer: {
    sectionLabel: 'Legal and corporate information',
    legalLinks: 'Legal links',
    myData: 'My Data',
    terms: 'Terms and conditions',
    privacy: 'Privacy',
    cookies: 'About cookies',
    copyright:
      '© 2025 Procter & Gamble. All rights reserved. The use of and access to the information on this site is subject to the terms and conditions set out in our legal agreement.',
  },

  status: {
    ticket: {
      approved: 'Approved',
      in_review: 'In review',
      declined: 'Declined',
    },
    challenge: {
      active: 'Active',
      finalized: 'Finalized',
    },
    withdrawal: {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
    },
    generic: {
      success: 'Success',
      warning: 'Warning',
      error: 'Error',
      info: 'Info',
      pending: 'Pending',
      active: 'Active',
      inactive: 'Inactive',
    },
  },

  quickUpload: {
    title: 'Upload Receipt',
    takePhoto: 'Tap to take a photo',
    fromGallery: 'or choose from the gallery',
    previewAlt: 'Receipt preview',
    removeImage: 'Remove image',
    selectChallenges: 'Select challenges',
    selectedCount: '{count} selected',
    noActiveChallenges: 'There are no active challenges right now',
    invalidImage: 'Please select a valid image',
    imageTooLarge: 'The image is too large (maximum 10MB)',
    noTicketCreated: 'No receipt could be created for the selected challenges.',
    uploadError: 'Error uploading the receipt',
    processing: 'Processing...',
    uploading: 'Uploading receipt... {progress}%',
    potentialEarnings: 'Potential earnings',
    disclaimer: 'Online purchase receipts are not accepted. Only physical store receipts.',
    submitUploading: 'Uploading...',
    submit: 'Upload to {count} challenge | Upload to {count} challenges',
  },

  quickWithdraw: {
    title: 'Fund transfer',
    requested: 'Transfer requested!',
    requestedDetail: 'It will be processed within 24-48 business hours',
    availableBalance: 'Available balance',
    insufficientBalance: 'Insufficient balance',
    minToWithdraw: 'You need at least {amount} to transfer',
    howMuch: 'How much do you want to transfer?',
    enterAmount: 'Enter amount',
    minAmount: 'The minimum amount is {amount}',
    exceedsBalance: 'The amount exceeds your available balance',
    selectedAmount: 'Selected amount:',
    destinationAccount: 'Destination account',
    verifiedAccount: 'Verified account',
    noBankAccount: 'Set up your bank account in your profile',
    infoNote: 'The transfer will be processed within 24-48 business hours',
    withdrawError: 'Error processing the transfer',
    submitProcessing: 'Processing...',
    submit: 'Request transfer {amount}',
  },

  brand: {
    title: 'P&G Health Contigo',
    titleShort: 'P&G Health',
    subtitle: 'Challenge Program for Pharmacies',
  },

  challenges: {
    placeholderLabel: 'Challenge',
    title: 'Available challenges',
    subtitle: 'Take part in the challenges and earn rewards by uploading your purchase receipts',
    loadMore: 'Load more challenges',
    showingCount: 'Showing {shown} of {total} challenges',
    filter: {
      status: 'Status',
      statusAll: 'All',
      statusActive: 'Active',
      statusFinalized: 'Finalized',
      brand: 'Brand',
      allBrands: 'All brands',
      search: 'Search',
      searchPlaceholder: 'Search challenges...',
      clear: 'Clear filters',
    },
    list: {
      empty: 'No challenges available',
      emptyDesc: 'Check back later for new challenges',
    },
    card: {
      variableReward: 'Variable reward',
      lastDay: 'Last day',
      daysRemaining: '{days} days remaining',
      until: 'Until {date}',
      finishedOn: 'Finished {date}',
      uploadTicket: 'Upload receipt',
    },
    detail: {
      fallbackTitle: 'Challenge detail',
      loading: 'Loading challenge detail...',
      imageAlt: 'Challenge image: {title}',
      reward: 'Reward',
      rewardPerTicket: 'Reward per valid receipt',
      description: 'Description',
      validityPeriod: 'Validity period',
      maxParticipations: 'Maximum participations',
      perUser: '{count} per user',
      viewTerms: 'View terms and conditions',
      opensNewWindow: '(opens in a new window)',
      notFound: 'Challenge not found',
    },
  },

  fields: {
    email: 'Email address',
    password: 'Password',
  },

  validation: {
    emailRequired: 'Email address is required',
    emailInvalid: 'Enter a valid email address',
    passwordRequired: 'Password is required',
    passwordMin: 'Password must be at least 8 characters',
    passwordUpper: 'Password must contain at least one uppercase letter',
    passwordLower: 'Password must contain at least one lowercase letter',
    passwordNumber: 'Password must contain at least one number',
    confirmRequired: 'Confirm your password',
    passwordsMismatch: 'Passwords do not match',
    codeRequired: 'The code is required',
    code6: 'The code must be 6 digits',
    nameRequired: 'First name is required',
    surnameRequired: 'Last name is required',
    nifRequired: 'CIF/NIF is required',
    nifInvalid: 'Enter a valid CIF/NIF',
    termsRequired: 'You must accept the terms and conditions',
    strength: {
      minLength: 'At least 8 characters',
      uppercase: 'At least one uppercase letter',
      lowercase: 'At least one lowercase letter',
      number: 'At least one number',
    },
  },

  password: {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    new: 'New password',
    confirm: 'Confirm password',
    requirementsTitle: 'The password must contain:',
    req8: 'At least 8 characters',
    reqUpper: 'One uppercase letter',
    reqLower: 'One lowercase letter',
    reqNumber: 'One number',
  },

  auth: {
    login: {
      feature1: 'Take part in exclusive challenges',
      feature2: 'Earn rewards for your sales',
      feature3: 'Check your balance in real time',
      feature4: 'EXCLUSIVE program for pharmacies that share sell-out data with P&G',
      title: 'Log in',
      welcome: 'Welcome back',
      sessionExpired: 'Your session has expired. Please log in again.',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot your password?',
      submit: 'Log in',
      noAccount: "Don't have an account? ",
      registerHere: 'Sign up here',
    },
    forgot: {
      subtitle: 'Reset password',
      stepRequest: 'Request',
      stepReset: 'Reset',
      resetComplete: 'Your password has been reset successfully. You will be redirected to the login page.',
      goLogin: 'Go to login',
      requestIntro: 'Enter your email address and we will send you a code to reset your password.',
      codeSent: 'We have sent a code to your email address. Check your inbox.',
      sendCode: 'Send code',
      resetIntroPre: 'Enter the 6-digit code we sent to',
      resetIntroPost: 'and your new password.',
      verificationCode: 'Verification code',
      resend: "Didn't receive the code? Resend",
      submitReset: 'Reset password',
    },
    activation: {
      title: 'Activate your account',
      methodEmail: 'Enter the code we sent to your email address',
      methodSms: 'Enter the code we sent to your mobile phone',
      methodCode: 'Enter the activation code you received',
      codeLabel: 'Activation code',
      codeRequired: 'The activation code is required',
      submit: 'Activate account',
    },
    register: {
      subtitle: 'Pharmacy owner or employee registration',
      type: 'Registration type',
      roleEmployee: 'Employee',
      roleOwner: 'Owner',
      name: 'First name *',
      surname: 'Last name *',
      email: 'Email address *',
      nif: 'CIF/NIF *',
      phone: 'Phone (optional)',
      activationCode: 'Activation code *',
      password: 'Password *',
      confirmPassword: 'Confirm password *',
      submitOwner: 'Create owner account',
      submitEmployee: 'Request employee registration',
      haveAccount: 'Already have an account? ',
      loginHere: 'Log in',
      linkLegalBases: 'legal terms',
      linkTerms: 'terms and conditions',
      linkHere: 'here',
      termsIntro: "Program rules: by registering for P&G Health's incentive program, I confirm that I have read and accept the",
      termsAnd: 'and the',
      termsParagraph2: 'Procter & Gamble ESPAÑA SAU, as the Data Controller, will process your personal data to register you on this site, interact with its services and send you communications relevant to the program.',
      termsParagraph3: 'You can unsubscribe from the program at any time from the "Privacy" section. You can read our privacy policy',
    },
    registerPending: {
      defaultMessage: 'Your profile has been created successfully! Please contact your pharmacy owner to approve it.',
      feature1: 'Owner or employee registration',
      feature2: 'Earn rewards for your sales',
      feature3: 'Check your balance in real time',
      title: 'Registration pending approval',
      emailLabel: 'Registered email: {email}',
      backToRegister: 'Back to registration',
      goLogin: 'Go to login',
    },
  },

  balance: {
    title: 'My Balance',
    subtitle: 'Check credits and deductions from completed external payments.',
    payoutInfo: 'The accumulated amount will be paid monthly to your pharmacy account in increments of €10. If you have not reached €10 by the payment date, the accumulated amount will be saved and paid the following month once you reach €10.',
    contributionByEmployee: 'Contribution by employee',
    movementHistory: 'Transaction history',
    contributorFallback: 'Employee',
    loading: 'Loading balance...',
    display: {
      pharmacyBalance: 'Pharmacy balance',
      yourEarnings: 'Your earnings',
      pharmacyTotalEarnings: 'Your pharmacy total earnings',
      yourContribution: 'Your contribution',
      pending: 'Pending',
      paid: 'Paid',
      totalEarned: 'Total earned',
      movementBreakdown: 'Transaction breakdown',
      movements: '{count} transactions',
      noEarnings: 'No challenges with earnings yet',
      challengeFallback: 'Challenge',
      breakdownTypes: {
        ticket_credit: 'Ticket credits',
        welcome_bonus: 'Welcome bonus',
        external_payout_deduction: 'External payment deduction',
        adjustment: 'Adjustments',
        reversal: 'Reversals',
      },
    },
    contributorBreakdown: {
      loading: 'Loading contribution breakdown...',
      empty: 'No contributions recorded yet.',
      contributor: 'Contributor',
      totalPending: 'Total pending',
      eligible: 'Payable',
      remainder: 'Remainder',
      inLastPayment: 'In last payment',
      totalPaid: 'Total paid',
      lastPayment: 'Last payment {date}',
      noPaymentsYet: 'No payments yet',
      notInLastPayment: 'Not part of the last payment',
    },
    summaryCards: {
      totalEarned: 'Total earned',
      eligibleBalance: 'Balance to pay',
      carryover: 'Accumulated remainder',
      availableBalance: 'Available balance',
      totalPaid: 'Total paid',
    },
    transactions: {
      loading: 'Loading transactions...',
      empty: 'No transactions recorded yet.',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {page} of {total}',
    },
    contributionTable: {
      searchLabel: 'Search employee',
      searchPlaceholder: 'Search employee...',
      searchAria: 'Search employee by name',
      loadingAria: 'Loading contributions',
      loadingList: 'Loading contribution data...',
      mobileAria: 'Contribution by employee on mobile',
      empty: 'No contributions recorded',
      ticketsApproved: '{count} approved receipts',
      lastActivity: 'Last activity',
      employee: 'Employee',
      ticketsApprovedHeader: 'Approved receipts',
      contribution: 'Contribution',
      summaryAria: 'Contributions summary',
      employees: 'Employees',
      totalTickets: 'Total receipts',
      totalContributions: 'Total contributions',
    },
    withdrawal: {
      availableToWithdraw: 'Balance available to pay out',
      invalidAmount: 'Enter a valid amount',
      exceedsBalance: 'The amount exceeds your available balance',
      noBankAccount: 'To request a payout you need to set up a bank account in your profile.',
      verifiedAccount: 'Verified account',
      amountLabel: 'Amount to pay out',
      minBtn: 'Min (€10)',
      importantInfo: 'Important information:',
      info1: 'The payout will be processed within 24-48 business hours',
      info2: 'An invoice will be generated automatically',
      info3: 'The minimum payout amount is €10',
      submit: 'Request payout',
      confirmMessage: 'Do you confirm the payout of {amount} to your registered bank account?',
      confirmHeader: 'Confirm payout',
      confirmAccept: 'Yes, pay out',
    },
  },

  notifications: {
    title: 'Notifications',
    subtitle: 'Persistent center for alerts and approval events.',
    filters: 'Filters',
    filterAll: 'All',
    filterUnread: 'Unread',
    unreadCount: '{count} unread',
    list: 'List',
    loading: 'Loading notifications...',
    empty: 'No notifications to show.',
    markRead: 'Mark as read',
    loadMore: 'Load more',
  },

  ownerDashboard: {
    loading: 'Loading owner dashboard...',
    defaultPharmacyName: 'Your pharmacy',
    ownerBadge: 'OWNER',
    availableBalance: 'AVAILABLE BALANCE',
    balanceDesc: 'Current pharmacy balance with external payments and deductions already reflected.',
    viewDetailAria: 'View pharmacy balance details',
    viewDetail: 'View details',
    approvalsAria: 'View {count} pending approvals',
    approvePending: 'Approve pending',
    notificationsAria: 'View {count} unread alerts',
    unreadNotices: 'Unread alerts',
  },

  approvals: {
    title: 'Employee approval',
    subtitle: 'Manage pending access requests for your pharmacy.',
    filterPending: 'Pending',
    filterApproved: 'Approved',
    filterRejected: 'Rejected',
    filterAll: 'All',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    statusPending: 'Pending',
    filter: 'Filter',
    requests: 'Requests',
    pendingCount: '{count} pending',
    loading: 'Loading requests...',
    empty: 'There are no requests for the selected filter.',
    employeeFallback: 'Employee',
    requestedOn: 'Requested {date}',
    reason: 'Reason: {reason}',
    rejectPlaceholder: 'Rejection reason (optional)',
    approve: 'Approve',
    reject: 'Reject',
  },

  tickets: {
    receiptPlaceholder: 'Receipt',
    view: {
      title: 'My Receipts',
      subtitle: 'Manage your purchase receipts and check their status',
      uploadTicket: 'Upload receipt',
      loading: 'Loading receipts...',
      summaryTotal: 'Total',
      summaryApproved: 'Approved',
      summaryInReview: 'In review',
      summaryDeclined: 'Declined',
      loadMore: 'Load more',
    },
    list: {
      tabAll: 'All',
      tabApproved: 'Approved',
      tabInReview: 'In review',
      tabDeclined: 'Declined',
      loadingAria: 'Loading receipts',
      loadingList: 'Loading receipt list...',
      emptyAll: 'You have no receipts yet',
      emptyApproved: 'You have no approved receipts',
      emptyInReview: 'You have no receipts in review',
      emptyDeclined: 'You have no declined receipts',
      ticketFallback: 'Receipt',
      processedOn: '• Processed {date}',
      approved: 'Approved',
      declinedFallback: 'Receipt declined',
      pendingReview: 'Pending review',
      thumbnailAlt: 'Receipt {id}',
      thumbnailUnavailable: 'Image not available for receipt {id}',
      itemAria: 'Receipt {title}, status: {status}',
      statusApproved: 'approved',
      statusDeclined: 'declined',
      statusInReview: 'in review',
    },
    upload: {
      uploadingFor: 'Uploading receipt for:',
      selectFile: 'Select file',
      dragImage: 'Drag an image here',
      orUsePicker: 'or use the picker to choose a file',
      formats: 'Formats: JPEG, PNG, WebP • Max. {size}MB',
      noFileSelected: 'No file selected',
      invalidFormat: 'Invalid format: {name}. Only images are allowed (JPEG, PNG, WebP).',
      fileTooLarge: 'File too large: {name}. The maximum size is {size}MB.',
      filesSelected: '{count} file(s) selected',
      processing: 'Processing image...',
      uploading: 'Uploading... {progress}%',
      previewTitle: 'Preview',
      previewAlt: 'Preview {index}',
    },
    uploadDialog: {
      header: 'Upload receipt',
      selectChallenges: 'Select challenges',
      placeholder: 'Select one or more challenges',
      oneChallenge: '1 challenge',
      challengesSelected: '{count} challenges selected',
      emptyState: 'Select one or more challenges to upload your receipt',
      success: 'Receipt submitted successfully! It will be reviewed shortly.',
    },
    detail: {
      fallbackTitle: 'Receipt detail',
      batchId: 'Batch ID',
      batchCopied: 'Copied to clipboard',
      batchCopyAria: 'Copy batch ID to clipboard',
      creditedAmount: 'Credited amount',
      declineReasonLabel: 'Decline reason:',
      declineReasonFallback: 'The receipt does not meet the campaign requirements.',
      // Keys mirror review_reason_codes codes; texts match the decline email.
      declineReasons: {
        NOT_ELIGIBLE: 'Not eligible',
        MISSING_INFORMATION: 'Missing information',
        DUPLICATE_RECEIPT: 'Duplicate receipt',
        FRAUD_SUSPECTED: 'Fraud suspected',
        NOT_READABLE: 'Not readable',
      },
      imageUnavailable: 'The receipt image is not available right now.',
      history: 'History',
      uploaded: 'Receipt uploaded',
      approved: 'Receipt approved',
      declined: 'Receipt declined',
      inReview: 'In review',
      associatedChallenge: 'Associated challenge',
      uploadDate: 'Upload date',
      processedDate: 'Processed date',
      notFound: 'Receipt not found',
      imageAlt: 'Receipt {id}',
    },
  },

  profile: {
    title: 'My Profile',
    subtitle: 'Manage your personal information and pharmacy details',
    loading: 'Loading profile...',
    loadError: 'Error loading your profile',
    personalData: 'Personal details',
    privacy: 'Privacy',
    privacyDescription:
      'Access GDPR features to export and delete your personal data.',
    openPrivacy: 'Open privacy',
    updateSuccess: 'Profile updated successfully',
    updateError: 'Error saving your changes',
    pharmacyTab: 'My pharmacy',
    pharmacyName: 'Name',
    pharmacyNif: 'CIF/NIF',
    pharmacyAddress: 'Address',
    pharmacyOwner: 'Owner',
    pharmacyTitle: 'My Pharmacy',
    pharmacyNotice: 'To change the pharmacy details, contact the administrator.',
    session: 'Session',
    logout: 'Log out',
  },

  bankAccount: {
    registeredTitle: 'Registered bank account',
    verified: 'Verified',
    pending: 'Pending',
    holder: 'Holder',
    iban: 'IBAN',
    bic: 'BIC/SWIFT',
    editAccount: 'Edit account',
    setupInfo: 'Set up your bank account to receive your balance payouts.',
    reenterIbanInfo: 'For security, re-enter the full IBAN to save changes.',
    holderName: 'Account holder name',
    holderNameHelp: 'Full name as it appears on the account',
    holderNameRequired: 'The account holder name is required',
    ibanRequired: 'IBAN is required',
    ibanInvalid: 'The IBAN is not valid',
    ibanHelp: 'Format: ES00 0000 0000 00 0000000000',
    bicOptional: 'BIC/SWIFT (optional)',
    bicHelp: '8 or 11 characters, e.g. CAIXESBBXXX',
    saveChanges: 'Save changes',
    saveAccount: 'Save account',
  },

  profileForm: {
    name: 'First name',
    surname: 'Last name',
    nif: 'NIF/NIE',
    nifReadonly: 'The NIF/NIE cannot be changed',
    email: 'Email',
    phone: 'Phone',
    phoneFormat: 'Format: 612 345 678',
    save: 'Save changes',
    nameRequired: 'First name is required',
    nameMin: 'First name must be at least 2 characters',
    nameLetters: 'First name can only contain letters',
    surnameRequired: 'Last name is required',
    surnameMin: 'Last name must be at least 2 characters',
    surnameLetters: 'Last name can only contain letters',
    emailRequired: 'Email is required',
    emailInvalid: 'Email is not valid',
  },

  privacy: {
    title: 'Privacy and GDPR',
    subtitle: 'Manage personal data export and deletion requests.',
    exportSuccess: 'Export complete. You can copy the JSON result.',
    exportError: 'Could not export your information.',
    eraseConfirmRequired: 'Type DELETE to confirm the irreversible deletion.',
    eraseError: 'Could not perform the data deletion.',
    exportTitle: 'Export my data',
    exportDesc: 'Generate a full export of your personal data stored on the platform.',
    generateExport: 'Generate export',
    eraseTitle: 'Request deletion',
    eraseDescPre: 'This action is irreversible and anonymizes your personal data. To confirm, type',
    eraseKeyword: 'DELETE',
    erasePlaceholder: 'Type DELETE',
    eraseButton: 'Delete my data',
  },

  legal: {
    basesTitle: 'Legal terms',
    basesSubtitle: 'Legal terms of the P&G Incentive Program - Pharmacies.',
    termsTitle: 'Terms and conditions',
    termsSubtitle: 'Legal terms of the P&G Incentive Program - Pharmacies.',
    backToRegister: 'Back to registration',
  },

  notFound: {
    backHome: 'Back to home',
    goLogin: 'Go to login',
    title: 'Page not found',
    description: 'The page you are looking for does not exist or has been moved. Check the URL or go back home.',
    goBack: 'Go back',
    needHelp: 'Need help?',
    contactSupport: 'Contact support',
  },
} as const
